import { QRCodeCanvas } from 'qrcode.react';
import React, { useState } from 'react';
import { TbCheck, TbCopy } from 'react-icons/tb';
import { convertDateToTimestamp, copyToClipboard, vibrate } from '../../../Utils/Helpers';
import Badge from '../Badge';
import CountdownExpirationDate from '../CountdownExpirationDate';
import ProgressBar from '../ProgressBar';
import Confirm from '../Confirm';
import Modal from '../Modal';
import { useCartHook } from '../../../Hooks/CartHook';
import ButtonPrimary from '../../Form/ButtonPrimary';
import { useCartContext } from '../../../Contexts/CartContext';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useNavigate } from 'react-router-dom';
import { useStoreHook } from '../../../Hooks/StoreHook';
import { useCheckoutContext } from '../../../Contexts/CheckoutContext';
import { useTranslation } from 'react-i18next';

interface QrCodeProps {
    onClose: CallableFunction
    order: any
    onExpire: CallableFunction
}
const QRCodePix = ({ order, onClose = () => {}, onExpire = () => {} }: QrCodeProps) => {

    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const { cancelPayment } = useCartHook();
    const { currentCart } = useCartContext();
    const { isStoreCounter } = useOrderTypeContext();
    const { setOrder } = useCheckoutContext();
    const navigate = useNavigate();
    const { getLinkToHome } = useStoreHook();

    const handleCopy = async () => {
        vibrate();
        const success = await copyToClipboard(order.payment_method.pix_qr_code);
        if (success) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }

    const handleCancel = () => {
        vibrate();
        cancelPayment(isStoreCounter ? order.cart.id : currentCart.cart_id)
            .then((response: any) => {
                if (response.success) {
                    navigate(`${getLinkToHome()}/checkout`);
                    setOrder(null);
                }
            });
        setShowConfirmCancel(false);
    }

    return (
        <div className="flex gap-3 flex-col md:max-w-2xl p-2">

            <div className='text-center leading-none text-base font-semibold'>
                {t('qrcode_pix.instruction')}
            </div>

            <div className='flex items-center justify-center'>
                <div className='p-3 my-4 ring-8 ring-black rounded shadow-lg shadow-black'>
                    <div className={`flex justify-center ${copied ? 'pointer-events-none' : 'cursor-pointer'}`} onClick={handleCopy}><QRCodeCanvas size={160} value={order.payment_method.pix_qr_code}  /></div>
                </div>
            </div>

            <div className={`flex flex-col gap-4 p-2 md:p-4 pt-0 ${copied && 'pointer-events-none'}`}>
                {/* <span className='text-gray-400 text-xs break-all'>{ order.payment_method.pix_qr_code }</span> */}

                <div className='flex items-center gap-3 px-1'>
                    <Badge type='required' className='flex items-center text-sm font-extrabold w-[80px]'>
                        <CountdownExpirationDate timestamp={order.payment_method.pix_expiration_date_timestamp * 1000} onComplete={onExpire()} />
                    </Badge>
                    <ProgressBar 
                        initialTimestamp={convertDateToTimestamp(order.timezone_created_at)} 
                        expirationTimestamp={order.payment_method.pix_expiration_date_timestamp * 1000} 
                    />
                </div>

                <div className='flex justify-between gap-2'>
                    <ButtonPrimary onClick={handleCopy} 
                        className={`gap-0.5 w-full justify-center leading-none text-sm
                            transition
                            ${copied ? 'text-white bg-shopfood-success hover:bg-shopfood-success ring-shopfood-success hover:ring-shopfood-success' : ''}
                        `}>
                        {!copied ? <TbCopy size={20} /> : <TbCheck size={20} />}
                        <span className='text-nowrap'>{!copied ? t('qrcode_pix.button_copy_code') : t('qrcode_pix.button_code_copied')}</span>
                    </ButtonPrimary>

                    {isStoreCounter &&
                        <ButtonPrimary outline onClick={() => setShowConfirmCancel(true)} className='text-nowrap'>
                            {t('qrcode_pix.button_cancel_pix')}
                        </ButtonPrimary>
                    }

                </div>
            </div>

            <Modal open={showConfirmCancel} className="max-w-sm m-4" onClose={() => setShowConfirmCancel(false)}>
                <Confirm
                    onConfirm={handleCancel} 
                    onCancel={() => setShowConfirmCancel(false)} 
                    title={t('qrcode_pix.confirm_cancel_title')}
                    message={t('qrcode_pix.confirm_cancel_message')}
                    labelConfirm={t('qrcode_pix.confirm_cancel_label_confirm')}
                    labelCancel={t('qrcode_pix.confirm_cancel_label_cancel')}
                />
            </Modal>

        </div>
    )
};

export default QRCodePix;
